import { request, toQuery, unwrap } from '../client';
import type {
  ApiResponse,
  CreateTenantRequest,
  OidcClientPickerDto,
  TenantDto,
  TenantPageParams,
  TenantPageResponse,
  TenantSignupMode,
  TenantStatus,
  UpdateTenantRequest,
} from '../types';

const BASE = '/api/saas-admin/tenants';

/** Server-side max `size` (SASATH-0212 clamps anything larger). */
const MAX_PAGE_SIZE = 200;

export const tenantsApi = {
  /**
   * Every tenant, as one array — a **temporary SASATH-0212 bridge**. The
   * endpoint now pages, so this walks it (largest page the server allows) and
   * concatenates rather than issuing a single unpaged read. The signature is
   * unchanged so the consumers that still need the whole set keep working:
   * `BrandEditor`, the `OidcClientCreate`/`OidcClientDetail` tenant pickers,
   * `ProductDetail`, and `TenantsList`. Later specs (SASADM-0107 onward) move
   * them onto `listPaged` with server-side filters, after which this goes away.
   */
  async list(): Promise<TenantDto[]> {
    const all: TenantDto[] = [];
    for (let page = 0; ; page += 1) {
      const res = await tenantsApi.listPaged({ page, size: MAX_PAGE_SIZE });
      all.push(...res.content);
      // `page + 1 >= totalPages` is the real termination. The other two bound
      // the loop against a malformed envelope, which must never be able to
      // hammer the server forever: a page with no rows but a stale
      // `totalPages`, and a missing/non-numeric `totalPages` (against which
      // `page + 1 >= undefined` is always false).
      if (
        res.content.length === 0 ||
        !Number.isFinite(res.totalPages) ||
        page + 1 >= res.totalPages
      ) {
        break;
      }
    }
    return all;
  },

  /**
   * SASATH-0212 — server-paged/filtered tenant read. `q` is a case-insensitive
   * contains match over slug + name; the filters (`productId`, `status`,
   * `brandId`, `productGroup`) are independent and AND-combined. Empty params
   * are omitted, so an empty `q` is never sent and the facet is requested only
   * when `includeGroupCounts` is true — which is also the only case in which
   * the response carries `productGroupCounts`.
   */
  async listPaged(params: TenantPageParams = {}): Promise<TenantPageResponse> {
    const res = await request<ApiResponse<TenantPageResponse>>('GET', BASE, {
      query: toQuery({
        page: params.page,
        size: params.size,
        q: params.q,
        productId: params.productId,
        status: params.status,
        brandId: params.brandId,
        productGroup: params.productGroup,
        includeGroupCounts: params.includeGroupCounts,
        sort: params.sort,
      }),
    });
    return unwrap(res);
  },

  async get(id: number): Promise<TenantDto> {
    const res = await request<ApiResponse<TenantDto>>('GET', `${BASE}/${id}`);
    return unwrap(res);
  },

  async create(req: CreateTenantRequest): Promise<TenantDto> {
    const res = await request<ApiResponse<TenantDto>>('POST', BASE, { json: req });
    return unwrap(res);
  },

  async update(id: number, req: UpdateTenantRequest): Promise<TenantDto> {
    const res = await request<ApiResponse<TenantDto>>('PUT', `${BASE}/${id}`, { json: req });
    return unwrap(res);
  },

  async setStatus(id: number, status: TenantStatus): Promise<TenantDto> {
    const res = await request<ApiResponse<TenantDto>>(
      'PUT',
      `${BASE}/${id}/status`,
      { query: { status } },
    );
    return unwrap(res);
  },

  async setSignupMode(id: number, mode: TenantSignupMode): Promise<TenantDto> {
    const res = await request<ApiResponse<TenantDto>>(
      'PUT',
      `${BASE}/${id}/signup-mode`,
      { query: { mode } },
    );
    return unwrap(res);
  },

  async setProduct(id: number, productId: number): Promise<TenantDto> {
    const res = await request<ApiResponse<TenantDto>>('PUT', `${BASE}/${id}/product`, {
      json: { productId },
    });
    return unwrap(res);
  },

  async setBrand(id: number, brandId: number | null): Promise<TenantDto> {
    const res = await request<ApiResponse<TenantDto>>('PUT', `${BASE}/${id}/brand`, {
      json: { brandId },
    });
    return unwrap(res);
  },

  // Spec 0058 (saas-auth SASATH-0076) — invitation OIDC-client picker. Lists the
  // OIDC clients whose allow-list includes this tenant (empty when none), used to
  // populate the invite form's OIDC-client multi-select (U4). Tenant-scoped path,
  // so it lives on `tenantsApi` alongside the other `tenants/{id}/*` reads.
  async oidcClientsForTenant(id: number): Promise<OidcClientPickerDto[]> {
    const res = await request<ApiResponse<OidcClientPickerDto[]>>(
      'GET',
      `${BASE}/${id}/oidc-clients`,
    );
    return unwrap(res);
  },
};
